import { TextButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MappingConfig } from 'apis/merchant-gateway/integrations-middleware-service/types';
import { Typography as T } from 'common/typography';

import SaveMappingConfigModal from './SaveMappingConfigModal';
import { ALL_MAPPING_TYPES, MappingOption } from './types';
import useVersionedMappingConfigs from '../hooks/useVersionedMappingConfigs';

type Props = {
  merchantId: string;
};

const Container = styled.div`
  margin-block-start: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 26px 24px;
  color: ${cbSemanticCssVars.textPrimary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

type ModalState = {
  config: MappingConfig | null;
  option: MappingOption | null;
  isOpen: boolean;
};

const MappingConfigManagement = ({ merchantId }: Props) => {
  const [modalState, setModalState] = useState<ModalState>({
    config: null,
    option: null,
    isOpen: false,
  });
  const { data: versionedConfig, isLoading, refresh } = useVersionedMappingConfigs(merchantId);
  const handleClose = () => {
    setModalState({ config: null, isOpen: false, option: null });
  };

  return (
    <>
      <Container>
        <Header>
          <T.Heading6>
            <FormattedMessage id="merchant-integrations.mapping-config.title" />
          </T.Heading6>
        </Header>
        {isLoading && (
          <LoadingSpinnerContainer>
            <Spinner size="large" />
          </LoadingSpinnerContainer>
        )}
        {!isLoading && (
          <DataTable
            dataSource={ALL_MAPPING_TYPES}
            getRowKey={record => record.type}
            horizontalPadding="wide"
            columns={[
              {
                id: 'type',
                name: 'Mapping type',
                render: ({ record }) => {
                  return <>{record.key}</>;
                },
              },
              {
                id: 'version',
                name: 'Version',
                render: ({ record }) => {
                  return versionedConfig.find(c => c.config?.type === record.type)?.version || 'N/A';
                },
              },
              {
                id: 'createdAt',
                name: 'Created at',
                render: ({ record }) => {
                  const createdAt = versionedConfig.find(c => c.config?.type === record.type)?.created_at;
                  if (createdAt) {
                    return `${DateTime.fromISO(createdAt).toRelative()} (${createdAt} UTC) `;
                  } else {
                    return;
                  }
                },
              },
              {
                id: 'actions',
                name: 'Actions',
                renderName: false,
                alignContent: 'right',
                render: ({ record }) => {
                  const config = versionedConfig.find(c => c.config?.type === record.type)?.config || null;
                  return (
                    <TextButton onClick={() => setModalState({ config, isOpen: true, option: record })}>
                      <FormattedMessage id="merchant-integrations.mapping-config.update-action" />
                    </TextButton>
                  );
                },
              },
            ]}
          />
        )}
      </Container>

      {modalState.option && (
        <SaveMappingConfigModal
          isOpen={modalState.isOpen}
          onSave={() => {
            handleClose();
            refresh();
          }}
          onClose={() => {
            handleClose();
          }}
          merchantId={merchantId}
          option={modalState.option}
          existingConfig={modalState.config}
        />
      )}
    </>
  );
};

export default MappingConfigManagement;
